import React, { useState } from 'react';
import { Activity, Play, Zap } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

type ElectricalAnalysisType = 'buck_ripple' | 'boost_ripple' | 'mosfet_loss' | 'opamp_stability' | 'rc_filter' | 'battery_soh' | 'battery_soc' | 'netlist_auto';

const ElectricalAnalysis: React.FC = () => {
    const [analysisType, setAnalysisType] = useState<ElectricalAnalysisType>('buck_ripple');
    const [params, setParams] = useState<{ [key: string]: string | number }>({});
    const [result, setResult] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    // Simplified options
    const analysisOptions = [
        { value: 'buck_ripple', label: 'Buck Converter Ripple' },
        { value: 'boost_ripple', label: 'Boost Converter Ripple' },
        { value: 'mosfet_loss', label: 'MOSFET Losses' },
        { value: 'battery_soh', label: 'Battery SoH' },
    ];

    const getFields = (type: ElectricalAnalysisType) => {
        // Basic implementation for a few types
        switch (type) {
            case 'buck_ripple': return [
                { key: 'vin', label: 'Vin (V)' }, { key: 'vout', label: 'Vout (V)' },
                { key: 'fsw_khz', label: 'Freq (kHz)' }, { key: 'L_uh', label: 'Inductance (µH)' },
                { key: 'C_uf', label: 'Capacitance (µF)' }, { key: 'iout_a', label: 'Iout (A)' }
            ];
            case 'battery_soh': return [
                { key: 'cycles_completed', label: 'Cycles' }, { key: 'avg_c_rate', label: 'Avg C-Rate' },
                { key: 'avg_temp_c', label: 'Avg Temp (°C)' }, { key: 'initial_capacity_ah', label: 'Initial Cap (Ah)' }
            ];
            default: return [];
        }
    };

    const handleCalculate = async () => {
        setLoading(true);
        try {
            const payload = {
                analysis_type: analysisType,
                parameters: Object.entries(params).reduce((acc, [k, v]) => ({
                    ...acc,
                    [k]: typeof v === 'string' && !isNaN(parseFloat(v)) ? parseFloat(v) : v
                }), {})
            };
            const data = await authenticatedFetchJSON('/api/v1/lab/electrical-analysis', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setResult(data);
        } catch {
            setResult({ error: "Analysis failed" });
        } finally {
            setLoading(false);
        }
    };

    const currentFields = getFields(analysisType);

    return (
        <div className="bg-card border border-border p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Zap className="text-yellow-500" /> Electrical Analysis
                </h2>
                <select
                    value={analysisType}
                    onChange={(e) => { setAnalysisType(e.target.value as ElectricalAnalysisType); setParams({}); setResult(null); }}
                    className="bg-background border border-border rounded px-2 py-1"
                >
                    {analysisOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    {currentFields.length > 0 ? currentFields.map(f => (
                        <div key={f.key}>
                            <label className="block text-xs font-medium mb-1 text-text-secondary">{f.label}</label>
                            <input type="number"
                                className="w-full bg-background border border-border rounded p-2 text-sm"
                                value={params[f.key] || ''}
                                onChange={e => setParams(prev => ({ ...prev, [f.key]: e.target.value }))}
                            />
                        </div>
                    )) : <p className="text-sm italic">Select a supported type</p>}

                    <button onClick={handleCalculate} disabled={loading} className="w-full bg-primary text-white py-2 rounded flex justify-center items-center gap-2">
                        {loading ? <Activity className="animate-spin" /> : <Play size={16} />} Analyze
                    </button>
                </div>
                <div className="bg-muted/20 border border-border rounded p-4 h-full min-h-[200px] overflow-auto">
                    <pre className="text-xs">{JSON.stringify(result, null, 2)}</pre>
                </div>
            </div>
        </div>
    );
};

export default ElectricalAnalysis;
